import { useEffect, useRef, useState } from "react";
import {
  Loader2, Copy, Save, RefreshCcw, Pencil, X, ChevronDown, ChevronUp,
  Lightbulb, Check, ThumbsDown, CheckCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Yusi Actions — Phase 2.
 * Expandable drawer rendered on a Task card. Shows the prepared content
 * Yusi generated for this task and exposes View / Copy / Edit / Save /
 * Regenerate. Scope is strictly content preparation — no publish, no
 * connect, no auto-apply.
 *
 * Bug-fix follow-up: the prepare endpoint is now background+polling
 * (mirrors /api/analyze) because production ingress timed out the
 * synchronous LLM call. POST returns a `run_id` immediately, this
 * component polls GET /tasks/prepare-jobs/{run_id} every 2.5s until the
 * job is done or errored. Quota is refunded server-side on failure.
 *
 * Props
 *   task: full task object from the backend (already lang-localized)
 *   onUpdated(updatedTask): called whenever the task changes (so the
 *     parent can refresh its list state without a full reload)
 */

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_ATTEMPTS = 60; // ~2.5min ceiling (server stale-cutoff is 10min)

export default function PreparedOutputDrawer({ task, onUpdated }) {
  const { lang, t } = useLang();
  const prepared = task.prepared_output || null;
  // Server-side states: "preparing" | "prepared" | "prepare_failed" | null
  const isPreparingState = task.action_state === "preparing";
  const isFailedState = task.action_state === "prepare_failed";

  const [open, setOpen] = useState(!!prepared);
  const [busy, setBusy] = useState(null); // "prepare" | "save" | "regenerate"
  const [polling, setPolling] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(prepared?.content || "");
  const pollTimerRef = useRef(null);

  // Cleanup any in-flight polling on unmount so we don't leak timers.
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const pollUntilDone = (runId, mode) =>
    new Promise((resolve, reject) => {
      let attempts = 0;
      const tick = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/tasks/prepare-jobs/${runId}`, {
            params: { lang },
          });
          if (data.status === "done") return resolve(data);
          if (data.status === "error") {
            return reject(new Error(data.error || t("tasks.prepareError")));
          }
          if (attempts >= POLL_MAX_ATTEMPTS) {
            return reject(new Error(t("tasks.prepareError")));
          }
          pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        } catch (err) {
          // 404 means the job vanished (cleared / never persisted). Bail out.
          reject(new Error(err?.response?.data?.detail || t("tasks.prepareError")));
        }
      };
      // Kick the first poll a bit later so the background task has a moment.
      pollTimerRef.current = setTimeout(tick, 800);
      void mode;
    });

  const startPrepare = async (mode = "prepare") => {
    if (mode === "regenerate" && prepared) {
      if (!window.confirm(t("tasks.drawer.regenerateConfirm"))) return;
    }
    setBusy(mode);
    setPolling(true);
    try {
      const { data: kicked } = await api.post(`/tasks/${task.id}/prepare`, { lang });
      if (!kicked?.run_id) throw new Error(t("tasks.prepareError"));
      const done = await pollUntilDone(kicked.run_id, mode);
      toast.success(t("tasks.prepareSuccess"));
      const next =
        done.task ||
        { ...task, prepared_output: done.prepared_output, action_state: "prepared" };
      setEditing(false);
      setDraft(done.prepared_output?.content || "");
      setOpen(true);
      onUpdated?.(next);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || t("tasks.prepareError");
      toast.error(msg);
      // Reflect the failed state locally so the CTA becomes clickable again
      // without a full task list refetch. The server already set
      // action_state="prepare_failed" via the background job's error path.
      onUpdated?.({ ...task, action_state: "prepare_failed" });
    } finally {
      setBusy(null);
      setPolling(false);
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
  };

  const copy = async () => {
    const text = editing ? draft : prepared?.content || "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("tasks.drawer.copyToast"));
    } catch {
      toast.error(t("tasks.drawer.saveError"));
    }
  };

  const save = async () => {
    if (!prepared) return;
    setBusy("save");
    try {
      const { data } = await api.patch(`/tasks/${task.id}/prepared-output`, {
        content: draft,
      }, { params: { lang } });
      toast.success(t("tasks.drawer.saveSuccess"));
      setEditing(false);
      onUpdated?.(data.task);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("tasks.drawer.saveError"));
    } finally {
      setBusy(null);
    }
  };

  // ---- Phase 3 approval actions ----
  // Each one posts to its own additive endpoint. None of them mutates
  // prepared_output content (so the user can always look back / regenerate
  // after a rejection). mark-used additionally triggers the same legacy
  // completion path as the existing "Uyguladım" button — there is exactly
  // one canonical "applied" transition on the server.
  const callApproval = async (mode) => {
    if (!prepared) return;
    if (mode === "used" && !window.confirm(t("tasks.drawer.markUsedConfirm"))) return;
    const path =
      mode === "approved"
        ? "approve-prepared-output"
        : mode === "rejected"
        ? "reject-prepared-output"
        : "mark-prepared-output-used";
    const successKey =
      mode === "approved"
        ? "tasks.drawer.approveSuccess"
        : mode === "rejected"
        ? "tasks.drawer.rejectSuccess"
        : "tasks.drawer.markUsedSuccess";
    setBusy(mode);
    try {
      const { data } = await api.post(`/tasks/${task.id}/${path}`, {}, { params: { lang } });
      toast.success(t(successKey));
      onUpdated?.(data.task);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("tasks.drawer.approvalError"));
    } finally {
      setBusy(null);
    }
  };

  // Approval chip state. action_state has priority because "used" wins
  // over any prior approval; otherwise fall back to approval_status; else
  // pending (we treat freshly-prepared output without an explicit
  // approval_status as "Onay bekliyor").
  const chipState = (() => {
    if (task.action_state === "used" || task.approval_status === "used") return "used";
    if (task.approval_status === "approved" || task.action_state === "approved") return "approved";
    if (task.approval_status === "rejected" || task.action_state === "rejected") return "rejected";
    if (prepared) return "pending";
    return null;
  })();
  const chipStyles = {
    pending: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    approved: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
    rejected: "bg-rose-500/15 text-rose-200 border-rose-500/30",
    used: "bg-cyan-500/20 text-cyan-100 border-cyan-500/40",
  };

  // No prepared output yet → show the "prepare" CTA only.
  if (!prepared) {
    const showSpinner = polling || busy === "prepare" || (isPreparingState && !isFailedState);
    return (
      <div className="mt-3 pt-3 border-t border-white/5">
        <button
          onClick={() => startPrepare("prepare")}
          disabled={polling || busy === "prepare"}
          className="w-full neon-btn rounded-md px-3 py-2 text-[11px] font-medium text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
          data-testid={`task-${task.id}-prepare`}
        >
          {showSpinner ? (
            <>
              <Loader2 size={12} className="animate-spin" />
              {t("tasks.preparingBtn")}
            </>
          ) : (
            <>
              <Lightbulb size={12} strokeWidth={2} />
              {t("tasks.prepareBtn")}
            </>
          )}
        </button>
        {isFailedState && !polling && (
          <p
            className="mt-2 text-[10px] text-amber-300/80"
            data-testid={`task-${task.id}-prepare-failed-hint`}
          >
            {t("tasks.prepareError")}
          </p>
        )}
      </div>
    );
  }

  // Has prepared content → toggleable drawer.
  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={`task-${task.id}-prepared-drawer`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-[11px] text-cyan-200 hover:text-cyan-100 transition-colors"
        data-testid={`task-${task.id}-prepared-toggle`}
      >
        <span className="inline-flex items-center gap-1.5">
          <Lightbulb size={12} strokeWidth={2} />
          {t("tasks.drawer.title")}
          <span className="text-slate-500 font-mono">
            · {t("tasks.drawer.versionLabel")} {prepared.version || 1}
          </span>
        </span>
        <span className="inline-flex items-center gap-2">
          {chipState && (
            <span
              className={`text-[9px] uppercase tracking-wider rounded-full border px-2 py-[1px] ${chipStyles[chipState]}`}
              data-testid={`task-${task.id}-approval-chip`}
            >
              {t(`tasks.approval.${chipState}`)}
            </span>
          )}
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      </button>

      {open && (
        <div className="mt-2 rounded-lg bg-white/[0.02] border border-white/10 p-3">
          <p
            className="text-[11px] text-slate-400 leading-relaxed mb-3"
            data-testid={`task-${task.id}-prepared-explainer`}
          >
            {t("tasks.drawer.explainer")}
          </p>
          {prepared.title && (
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
              {prepared.title}
            </div>
          )}

          {editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="w-full min-h-[140px] rounded-md bg-black/30 border border-white/10 p-2 text-xs text-slate-100 font-mono leading-relaxed focus:outline-none focus:border-cyan-400/40"
              data-testid={`task-${task.id}-prepared-editor`}
            />
          ) : (
            <pre
              className="whitespace-pre-wrap text-xs text-slate-200 leading-relaxed font-mono max-h-72 overflow-y-auto"
              data-testid={`task-${task.id}-prepared-view`}
            >
              {prepared.content}
            </pre>
          )}

          {!editing && Array.isArray(prepared.tips) && prepared.tips.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">
                {t("tasks.drawer.tipsTitle")}
              </div>
              <ul className="space-y-1">
                {prepared.tips.map((tip, i) => (
                  <li key={i} className="text-[11px] text-slate-300 leading-relaxed">
                    · {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-white/5">
            <button
              onClick={copy}
              className="ghost-btn rounded-md px-2.5 py-1.5 text-[10px] text-white inline-flex items-center gap-1"
              data-testid={`task-${task.id}-prepared-copy`}
            >
              <Copy size={10} /> {t("tasks.drawer.copyBtn")}
            </button>

            {editing ? (
              <>
                <button
                  onClick={save}
                  disabled={busy === "save"}
                  className="neon-btn rounded-md px-2.5 py-1.5 text-[10px] text-white inline-flex items-center gap-1 disabled:opacity-60"
                  data-testid={`task-${task.id}-prepared-save`}
                >
                  {busy === "save" ? (
                    <Loader2 size={10} className="animate-spin" />
                  ) : (
                    <Save size={10} />
                  )}
                  {t("tasks.drawer.saveBtn")}
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setDraft(prepared.content || "");
                  }}
                  className="ghost-btn rounded-md px-2.5 py-1.5 text-[10px] text-slate-300 inline-flex items-center gap-1"
                  data-testid={`task-${task.id}-prepared-cancel`}
                >
                  <X size={10} /> {t("tasks.drawer.cancelEditBtn")}
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setDraft(prepared.content || "");
                  setEditing(true);
                }}
                className="ghost-btn rounded-md px-2.5 py-1.5 text-[10px] text-white inline-flex items-center gap-1"
                data-testid={`task-${task.id}-prepared-edit`}
              >
                <Pencil size={10} /> {t("tasks.drawer.editBtn")}
              </button>
            )}

            <button
              onClick={() => startPrepare("regenerate")}
              disabled={polling || busy === "regenerate"}
              className="ghost-btn rounded-md px-2.5 py-1.5 text-[10px] text-white inline-flex items-center gap-1 disabled:opacity-60"
              data-testid={`task-${task.id}-prepared-regenerate`}
            >
              {polling || busy === "regenerate" ? (
                <Loader2 size={10} className="animate-spin" />
              ) : (
                <RefreshCcw size={10} />
              )}
              {polling || busy === "regenerate"
                ? t("tasks.preparingBtn")
                : t("tasks.regenerateBtn")}
            </button>
          </div>

          {/* Phase 3 — approval row. Buttons stay visible across states
              so the user can always change their mind (approve → reject,
              reject → approve, then mark-used when actually applied). */}
          <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-white/5">
            <button
              onClick={() => callApproval("approved")}
              disabled={busy === "approved"}
              className="rounded-md px-2.5 py-1.5 text-[10px] text-emerald-100 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 inline-flex items-center gap-1 disabled:opacity-60"
              data-testid={`task-${task.id}-prepared-approve`}
            >
              {busy === "approved" ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
              {t("tasks.drawer.approveBtn")}
            </button>
            <button
              onClick={() => callApproval("rejected")}
              disabled={busy === "rejected"}
              className="rounded-md px-2.5 py-1.5 text-[10px] text-rose-100 border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 inline-flex items-center gap-1 disabled:opacity-60"
              data-testid={`task-${task.id}-prepared-reject`}
            >
              {busy === "rejected" ? <Loader2 size={10} className="animate-spin" /> : <ThumbsDown size={10} />}
              {t("tasks.drawer.rejectBtn")}
            </button>
            <button
              onClick={() => callApproval("used")}
              disabled={busy === "used"}
              className="rounded-md px-2.5 py-1.5 text-[10px] text-cyan-100 border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 inline-flex items-center gap-1 disabled:opacity-60"
              data-testid={`task-${task.id}-prepared-mark-used`}
            >
              {busy === "used" ? <Loader2 size={10} className="animate-spin" /> : <CheckCheck size={10} />}
              {t("tasks.drawer.markUsedBtn")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
